import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import classnames from 'classnames';

import { withExperiment } from '@sparefoot/react-core/decorators';

import { LoadingMask } from 'components/core/LoadingMask';
import { Button } from 'components/core/Button';
import { Paragraph, Text } from 'components/core/Typography';
import { Image } from 'components/core/Image';
import { Input } from 'components/core/Input';
import {
	createAutocomplete,
	removeAutocomplete,
	formatPlaceObject,
} from 'utils/search/autocomplete';
import alert from 'assets/images/icons/alert-red.svg';
import MapPin from 'components/svg/MapPin';

import './GoogleAutocomplete.scss';

export const GoogleAutocomplete = ({
	onChange,
	name,
	getRef: getRefProp,
	value,
	label = 'Enter Zip or City Name',
	id,
	placeholder = 'Enter Zip or City Name',
	className,
	useBrowserLocation,
	hideOnScroll = false,
	geolocation: geolocationProp,
	sf15652Variation,
}) => {
	const storeGeolocation = useSelector((state) => state.user?.geolocation);
	const geolocation =
		geolocationProp === undefined ? storeGeolocation : geolocationProp;
	const [isFocused, setIsFocused] = useState(false);
	const [locating, setLocating] = useState(false);
	const [errorMessage, setErrorMessage] = useState(false);

	const textInputRef = useRef(null);
	const autoCompleteRef = useRef(null);
	const observerRef = useRef(null);

	const onAutocomplete = useCallback(
		(val, place) => {
			setIsFocused(false);
			const locationParams = formatPlaceObject(place);
			onChange({
				...locationParams,
				location: val,
			});
		},
		[onChange],
	);

	// we can't test this because it requires the actual google autocomplete to load in the DOM
	/* istanbul ignore next */
	const handleHideOnScroll = useCallback((e) => {
		e.preventDefault();
		const GoogleAutocompleteDrawerEl =
			document.querySelector('.pac-container');
		if (
			GoogleAutocompleteDrawerEl &&
			GoogleAutocompleteDrawerEl.style.display !== 'none' &&
			textInputRef.current === document.activeElement
		) {
			GoogleAutocompleteDrawerEl.style.display = 'none';
		}
	}, []);

	const onGetLocation = useCallback(
		(geo) => {
			setLocating(true);
			if (geo?.city && geo?.region && geo?.country) {
				const location =
					geo.city + ' ' + geo.region + ', ' + geo.country;
				onChange({ location });
				setIsFocused(false);
				setLocating(false);
				setErrorMessage(false);
			} else {
				setIsFocused(false);
				setLocating(false);
				setErrorMessage(true);
			}
		},
		[onChange],
	);

	const handleChange = useCallback(
		({ target }) => {
			const { value: val } = target;
			onChange({ location: val });
		},
		[onChange],
	);

	const onFocus = useCallback(() => {
		if (!autoCompleteRef.current) {
			createAutocomplete(textInputRef.current, onAutocomplete).then(
				(autoComplete) => {
					autoCompleteRef.current = autoComplete;
				},
			);
		}
		setIsFocused(true);
		setErrorMessage(false);
	}, [onAutocomplete]);

	const onBlur = useCallback(() => {
		setTimeout(() => setIsFocused(false), 250);
	}, []);

	const bindRef = useCallback(
		(ref) => {
			textInputRef.current = ref;
			if (getRefProp) {
				getRefProp(ref);
			}
		},
		[getRefProp],
	);

	useEffect(() => {
		if (hideOnScroll) {
			window.addEventListener('scroll', handleHideOnScroll);
		}

		observerRef.current = new MutationObserver(() => {
			const GoogleAutocompleteDrawerEl =
				document.querySelector('.pac-container');
			if (
				GoogleAutocompleteDrawerEl &&
				sf15652Variation === 'variation1'
			) {
				GoogleAutocompleteDrawerEl.style.transform = 'translateY(28px)';
				observerRef.current.disconnect();
			}
		});

		observerRef.current.observe(document.body, {
			childList: true,
			subtree: true,
		});

		return () => {
			if (observerRef.current) {
				observerRef.current.disconnect();
			}
			removeAutocomplete(autoCompleteRef.current);
			autoCompleteRef.current = null;
			if (hideOnScroll) {
				window.removeEventListener('scroll', handleHideOnScroll);
			}
			window.addEventListener('scroll', () => {}, {
				passive: false,
			});
		};
	}, []); // eslint-disable-line react-hooks/exhaustive-deps

	const sf15652Variation1 = sf15652Variation === 'variation1';

	return (
		<div
			className={classnames({
				[`${className}-wrapper`]: className,
			})}
		>
			<Input
				id={id}
				autoComplete="off"
				name={name}
				label={label}
				className={className}
				placeholder={placeholder}
				getRef={bindRef}
				onFocus={onFocus}
				onBlur={onBlur}
				onChange={handleChange}
				value={value}
			/>
			{useBrowserLocation && isFocused && sf15652Variation1 && (
				<Button
					className="use-your-location"
					onClick={() => onGetLocation(geolocation)}
				>
					<MapPin fill="black" />
					<Text className="text">Use My Location</Text>
				</Button>
			)}
			{errorMessage && (
				<div className="error-message">
					<Image
						src={alert}
						alt="Info"
						className="alert-icon"
					/>
					<Paragraph last>
						{
							'We weren\u2019t able to get your location. Please type it in manually.'
						}
					</Paragraph>
				</div>
			)}
			{locating && <LoadingMask modal />}
		</div>
	);
};

GoogleAutocomplete.propTypes = {
	onChange: PropTypes.func.isRequired,
	name: PropTypes.string,
	getRef: PropTypes.func,
	value: PropTypes.string,
	label: PropTypes.string,
	id: PropTypes.string,
	placeholder: PropTypes.string,
	className: PropTypes.string,
	useBrowserLocation: PropTypes.bool,
	hideOnScroll: PropTypes.bool,
	geolocation: PropTypes.object,
	sf15652Variation: PropTypes.string,
};

export default withExperiment('sf_15652_geolocation_ssdc')(GoogleAutocomplete);
